import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ContainerType } from '../../../shared/models/container.types';
import { catchError, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import { of, Subject, tap } from 'rxjs';
import { SplitScreenTab } from '../../model/split-screen-tabs';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TrainingStatus } from '../../../shared/models/training-status';
import { TrainingType } from '../../../shared/enums/training-type.enum';
import { isNil } from '../../../shared/utility';
import { TrainingService } from '../../../shared/services/training.service';
import { TrainingDTO } from '../../../shared/models/training-dto';
import { fadeIn, fadeOut } from '../../../shared/animations';
import { TreeStateService } from '../../../split-screen/services/tree-state.service';
import { TrainingTreeNode } from '../../../split-screen/models/training-tree-node';
import { TreeActionsService } from '../../../split-screen/services/tree-actions.service';
import { BeforeTrainingStart } from '../../../shared/interfaces/before-training-start';
import { AuthenticationService } from '../../../authentication/services/authentication.service';

@Component({
    selector: 'app-project-split-screen-tabs',
    templateUrl: './project-split-screen-tabs.component.html',
    styleUrls: ['./project-split-screen-tabs.component.scss'],
    animations: [fadeIn, fadeOut],
    standalone: false,
})
export class ProjectSplitScreenTabsComponent implements OnInit, OnDestroy {
    protected readonly SplitScreenTab = SplitScreenTab;
    protected readonly TrainingStatus = TrainingStatus;
    protected readonly ContainerType = ContainerType;

    @Input() containerType: ContainerType;

    @ViewChild('beforeStartComponent')
    currentChild?: BeforeTrainingStart;

    training: TrainingDTO;
    trainingId: string;
    trainingName: string;
    trainingType: TrainingType;

    tabs: SplitScreenTab[];
    selectedTab: SplitScreenTab;

    $destroy: Subject<void> = new Subject();
    trainingLoaded: boolean = false;
    editingIsDisabled: boolean = false;
    isExperimental: boolean = false;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private trainingService: TrainingService,
        private treeStateService: TreeStateService,
        private treeActionsService: TreeActionsService,
        private authenticationService: AuthenticationService
    ) {}

    ngOnInit(): void {
        this.isExperimental = this.authenticationService.isExperimental();
        this.tabs = this.buildTabsBasedOnContainerType();

        this.activatedRoute.queryParamMap
            .pipe(takeUntil(this.$destroy))
            .subscribe((params: ParamMap) => {
                const trainingId: string = params.get('trainingUuid');
                if (isNil(trainingId) || trainingId === '') {
                    return;
                }

                if (this.trainingId !== trainingId) {
                    this.trainingLoaded = false;
                    this.trainingId = trainingId;
                    this.fetchTraining();
                }

                const urlSelectedTab: string = params.get('selectedTab');
                if (isNil(urlSelectedTab)) {
                    this.onTabClick(this.selectedTab);
                } else {
                    const selectedTab: SplitScreenTab =
                        urlSelectedTab as SplitScreenTab;

                    this.selectedTab = this.tabs.includes(selectedTab)
                        ? selectedTab
                        : undefined;
                }
            });

        this.treeStateService.selectedTreeNode$
            .pipe(takeUntil(this.$destroy))
            .subscribe((treeNode: TrainingTreeNode) => {
                this.trainingName = treeNode.name;
                this.editingIsDisabled = treeNode.status !== TrainingStatus.NEW;
            });

        /**
         * If startTriggered emits "A" (with currentChild), triggering saveBeforeStart, and then
         * startTriggered emits "B" (without currentChild) while the save for "A" is still in
         * progress, the following will happen: mergeMap will receive the emission for "A" and begin
         * the saveBeforeStart operation. mergeMap will then receive the emission for "B" and,
         * because mergeMap processes observables concurrently, it will immediately process the
         * emission for "B" without waiting for the save operation for "A" to complete. This means
         * the outer subscribe block will be triggered for "B", and the canStart event for "B" will
         * be emitted, potentially before the save operation for "A" completes.
         */
        this.treeActionsService.$startTriggered
            .pipe(
                takeUntil(this.$destroy),
                mergeMap((nodeId: string) => {
                    if (this.currentChild) {
                        return this.currentChild.saveBeforeStart().pipe(
                            map(() => {
                                return { nodeId };
                            }),
                            catchError((error) => {
                                return of({ nodeId, error });
                            })
                        );
                    } else {
                        return of({ nodeId });
                    }
                }),
                tap((result: { nodeId: string; error?: Error }) => {
                    this.treeActionsService.$canStart.next(result);
                })
            )
            .subscribe();
    }

    private fetchTraining() {
        this.trainingService
            .get(this.trainingId)
            .pipe(take(1))
            .subscribe((training: TrainingDTO) => {
                this.training = training;
                this.trainingName = training.name;
                this.trainingType = training.trainingType;
                this.trainingLoaded = true;
            });
    }

    onTabClick(selectedTab: SplitScreenTab) {
        if (this.tabs.includes(selectedTab)) {
            this.router.navigate([], {
                skipLocationChange: false,
                relativeTo: this.activatedRoute,
                queryParams: {
                    selectedTab: selectedTab,
                },
                queryParamsHandling: 'merge',
            });
        }
    }

    getTranslationKey(selectedTab: SplitScreenTab) {
        if (this.tabs.includes(selectedTab)) {
            return `projectSplitScreen.components.tabs.${selectedTab}`;
        }
        return null;
    }

    ngOnDestroy(): void {
        this.$destroy.complete();
    }

    private buildTabsBasedOnContainerType(): SplitScreenTab[] {
        let defaultTabs: SplitScreenTab[] = [
            SplitScreenTab.ALGORITHM,
            SplitScreenTab.EVALUATION,
        ];

        // Add NETWORK_ARCHITECTURE for motoric trainings or if user has experimental role
        if (
            this.containerType === ContainerType.PROJECT_MOTORIC ||
            this.isExperimental
        ) {
            defaultTabs.splice(1, 0, SplitScreenTab.NETWORK_ARCHITECTURE);
        }

        switch (this.containerType) {
            case ContainerType.PROJECT_VISUAL:
                defaultTabs = [SplitScreenTab.IMAGE_DB].concat(defaultTabs);
                this.selectedTab = SplitScreenTab.IMAGE_DB;
                return defaultTabs;
            case ContainerType.PROJECT_MOTORIC:
                defaultTabs = [
                    SplitScreenTab.SCENE,
                    SplitScreenTab.COST_FUNCTION,
                ].concat(defaultTabs);
                this.selectedTab = SplitScreenTab.SCENE;
                return defaultTabs;
            default:
                return [];
        }
    }
}
